import AnalysisBox from '@/components/core/AnalysisBox';
import AddPathwayToProjectModal from '@/components/home/AddPathwayToProjectModal';
import PathwayTemplatesSearch from '@/components/pathwayNavigator/PathwayTemplatesSearch';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { Link } from 'react-router';
import { numberFormat, unitToString } from '@/utils/unitUtils';
import { usePathwayTemplates, usePathwayTemplatesMeta, pathwayTemplatesActions } from '@/stores/pathwayTemplatesStore';
import LoadMoreButton from '@/components/core/LoadMoreButton';

const PathwayNavigatorListRoute = () => {
  const pathwayTemplates = usePathwayTemplates();
  const { next } = usePathwayTemplatesMeta();
  const [selectedTemplateIds, setSelectedTemplateIds] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const toggleTemplateSelection = templateId => {
    setSelectedTemplateIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      await pathwayTemplatesActions.loadMorePathwayTemplates();
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <>
      <div className="overflow-y-auto p-4">
        <div className="mb-4">
          <PathwayTemplatesSearch />
        </div>
        <AnalysisBox>
          <table className="w-full text-left border-collapse">
            <thead className="bg-white">
              <tr className="border-b">
                <th className="py-2 px-2"></th>
                <th className="py-2 px-4 font-medium">Template Name</th>
                <th className="py-2 px-4 font-medium">Feedstock(s)</th>
                <th className="py-2 px-4 font-medium">Framework</th>
                <th className="py-2 px-4 font-medium">Cost</th>
                <th className="py-2 px-4 font-medium">Emissions</th>
                <th className="py-2 px-4 font-medium">Location & Year</th>
                <th className="py-2 px-4 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {pathwayTemplates.map((template, index) => (
                <tr
                  key={index}
                  className={`border-b hover:bg-gray-50 cursor-pointer text-sm ${
                    selectedTemplateIds.has(template.id) ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => toggleTemplateSelection(template.id)}
                >
                  <td className="py-3 px-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={selectedTemplateIds.has(template.id)}
                      onChange={() => {}}
                    />
                  </td>
                  <td className="py-3 px-4">{template.name}</td>
                  <td className="py-3 px-4">{template?.feedstocks?.join(', ')}</td>
                  <td className="py-3 px-4">{template.frameworks?.join(', ')}</td>
                  <td className="py-3 px-4 font-bold">
                    {template.cost?.scalar
                      ? `${numberFormat(template.cost.scalar)} ${unitToString(template.cost.unit)}`
                      : 'N/A'}
                  </td>
                  <td className="py-3 px-4 font-bold">
                    {template.emissions?.scalar && template.emissions?.unit
                      ? `${numberFormat(template.emissions.scalar)} ${unitToString(template.emissions.unit)}`
                      : 'N/A'}
                  </td>
                  <td className="py-3 px-4">
                    <span className="badge badge-sm badge-secondary">{template.locations?.join(', ')}</span>{' '}
                    <span className="badge badge-sm badge-secondary">{template.year}</span>
                  </td>
                  <td className="py-3 px-4">
                    <Link to={`${template.id}`} onClick={e => e.stopPropagation()}>
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <LoadMoreButton
            onLoadMore={handleLoadMore}
            loading={loadingMore}
            hasMore={!!next}
            className="btn btn-sm btn-outline"
          />
        </AnalysisBox>
      </div>

      {selectedTemplateIds.size > 0 && (
        <div className="absolute bottom-6 right-8 z-50">
          <button
            className="px-4 py-2 rounded text-sm flex items-center gap-2 btn btn-accent "
            onClick={() => setShowModal(true)}
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add to project {selectedTemplateIds.size > 0 && `(${selectedTemplateIds.size})`}</span>
          </button>
        </div>
      )}

      {showModal && (
        <AddPathwayToProjectModal selectedTemplateIds={selectedTemplateIds} onClose={() => setShowModal(false)} />
      )}
    </>
  );
};

export default PathwayNavigatorListRoute;
